import { CustomerPortalInputType, PORTAL_PAGES } from '@/constants'
import type { ConfigurablePage } from '@/types'

// Route matching is on the path alone — a query (?subscription=…) or hash belongs to the
// page's own state, not to which step it is.
function routePath(path: string): string {
  return path.split(/[?#]/)[0]
}

const ITEM_ROUTE = /^\/portal\/(orders|subscriptions)\/([^/]+)/

// Maps an OMP route to the customer-portal step type the backend fires on entry.
// Home 30, Orders 31, Subscriptions 32, SubscriptionDetail 33, PersonalInfo 34
// (the account/address/payment page), OrderDetail 35.
export function inputTypeForPath(path: string): number | undefined {
  const pathname = routePath(path)
  const kind = ITEM_ROUTE.exec(pathname)?.[1]
  if (kind === 'orders') {
    return CustomerPortalInputType.CustomerPortalOrderDetail
  }
  if (kind === 'subscriptions') {
    return CustomerPortalInputType.CustomerPortalSubscriptionDetail
  }
  if (pathname.startsWith('/portal/orders')) {
    return CustomerPortalInputType.CustomerPortalOrders
  }
  if (pathname.startsWith('/portal/subscriptions')) {
    return CustomerPortalInputType.CustomerPortalSubscriptions
  }
  if (pathname.startsWith('/portal/account')) {
    return CustomerPortalInputType.CustomerPortalPersonalInfo
  }
  if (pathname === '/portal' || pathname === '/portal/') {
    return CustomerPortalInputType.CustomerPortalHome
  }
  return undefined
}

// Detail routes carry an item id, so re-entering them must re-submit to move the
// server's selected item. Every other portal route is item-agnostic.
export function isItemScopedPath(path: string): boolean {
  return ITEM_ROUTE.test(routePath(path))
}

// The item id a detail route is keyed by, so the server can be told which item was opened.
// Plain safe integers go as numbers (so '007' or huge ids are not altered); any other segment is passed through so it is never dropped.
export function itemIdFromPath(path: string): number | string | undefined {
  const segment = ITEM_ROUTE.exec(routePath(path))?.[2]
  if (segment === undefined) {
    return undefined
  }
  let id = segment
  try {
    id = decodeURIComponent(segment)
  } catch {
    // Malformed escape: keep the raw segment rather than fail the navigation.
  }
  return String(Number(id)) === id && Number.isSafeInteger(Number(id)) ? Number(id) : id
}

// Payload for submitting `inputId` on the way to `path`; detail routes also carry the selected item.
export function portalInputParams(
  inputId: number | string,
  path: string
): { input: number | string; item?: number | string } {
  const item = itemIdFromPath(path)
  return item === undefined ? { input: inputId } : { input: inputId, item }
}

// A detail step is only reachable from its own list step — the funnel models no direct edge
// from, say, Orders to Subscription Details.
export function parentStepType(type: number): number | undefined {
  switch (type) {
    case CustomerPortalInputType.CustomerPortalSubscriptionDetail:
      return CustomerPortalInputType.CustomerPortalSubscriptions
    case CustomerPortalInputType.CustomerPortalOrderDetail:
      return CustomerPortalInputType.CustomerPortalOrders
    default:
      return undefined
  }
}

// Reverse map used after an undo to sync the URL to the restored step.
// Detail steps are omitted — they need an id, so we keep the URL the browser restored.
export function pathForStepType(stepType: number | string | undefined): string | undefined {
  switch (Number(stepType)) {
    case CustomerPortalInputType.CustomerPortalHome:
      return '/portal'
    case CustomerPortalInputType.CustomerPortalOrders:
      return '/portal/orders'
    case CustomerPortalInputType.CustomerPortalSubscriptions:
      return '/portal/subscriptions'
    case CustomerPortalInputType.CustomerPortalPersonalInfo:
      return '/portal/account'
    default:
      return undefined
  }
}

// Entry route for a portal session (preview links / deep links / OMP start).
// Unlike pathForStepType, this resolves detail pages too, pulling the id from
// the session's selected_item; falls back to the list page when it's absent.
export function portalEntryPath(session: any): string {
  const stepType = Number(session?.step_type)
  const selected = session?.selected_item
  switch (stepType) {
    case CustomerPortalInputType.CustomerPortalOrders:
      return '/portal/orders'
    case CustomerPortalInputType.CustomerPortalSubscriptions:
      return '/portal/subscriptions'
    case CustomerPortalInputType.CustomerPortalPersonalInfo:
      return '/portal/account'
    case CustomerPortalInputType.CustomerPortalOrderDetail: {
      // Keyed by order-item id, same as subscription details.
      return selected?.id != null ? `/portal/orders/${selected.id}` : '/portal/orders'
    }
    case CustomerPortalInputType.CustomerPortalSubscriptionDetail: {
      const subId = selected?.id
      return subId != null ? `/portal/subscriptions/${subId}` : '/portal/subscriptions'
    }
    case CustomerPortalInputType.CustomerPortalHome:
    default:
      return '/portal'
  }
}

// The input to submit lives in side_menu_inputs for top-level pages, or in
// valid_inputs/additional_inputs for step-specific ones like OrderDetail.
export function findPortalInput(session: any, type: number): any | undefined {
  const pools = [session?.side_menu_inputs, session?.valid_inputs, session?.additional_inputs]
  for (const pool of pools) {
    if (Array.isArray(pool)) {
      const found = pool.find((i: any) => Number(i?.type) === type)
      if (found) {
        return found
      }
    }
  }
  return undefined
}

export function pageFromPath(path: string): ConfigurablePage {
  const pathname = routePath(path)
  if (pathname.startsWith('/portal/subscriptions/') && pathname !== '/portal/subscriptions') {
    return PORTAL_PAGES.SubscriptionDetails
  }
  if (pathname.startsWith('/portal/subscriptions')) {
    return PORTAL_PAGES.Subscriptions
  }
  if (pathname.startsWith('/portal/orders/') && pathname !== '/portal/orders') {
    return PORTAL_PAGES.OrderDetails
  }
  if (pathname.startsWith('/portal/orders')) {
    return PORTAL_PAGES.Orders
  }
  return PORTAL_PAGES.Home
}

export function pathForPage(page: ConfigurablePage): string {
  switch (page) {
    case PORTAL_PAGES.Orders:
    case PORTAL_PAGES.OrderDetails:
      return '/portal/orders'
    case PORTAL_PAGES.Subscriptions:
    case PORTAL_PAGES.SubscriptionDetails:
      return '/portal/subscriptions'
    default:
      return '/portal'
  }
}
